import { BaseFilePickerInput } from 'components/BaseFilePicker/BaseFilePickerInput';
import {
  getBaseFilePickerDefaultMessage,
  getBaseFilePickerHelpText,
} from 'components/BaseFilePicker/baseFilePickerMappings';
import { useBaseFilePickerInputStateAndHandlers } from 'components/BaseFilePicker/useBaseFilePickerInputStateAndHandlers';
import { FieldError } from 'components/Form/Field/FieldError';
import { Icon } from 'components/Icon/Icon';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import type { FileUploadStateAndActions } from 'hooks/useFileUpload';
import { useI18n } from 'i18n/hooks/useI18n';

import { SmallFilePickerFilename } from './Filename/SmallFilePickerFilename';
import {
  SmallFilePickerChooseOrDragItHere,
  SmallFilePickerContent,
  SmallFilePickerNoFileChosenHelpText,
  SmallFilePickerWrapper,
} from './SmallFilePicker.styled';

type Props = {
  name: string;
  required: boolean;
  ariaLabel: string | null;
  accept: string | null;
  showDate: boolean;
  disabled: boolean;
  qaId: string;
  downloadAttachment: boolean;
  customMessage: string | null;
  maxSizeInMB: number | null;
  fileUploadStateAndActions: FileUploadStateAndActions;
};

export function SmallFilePicker({
  name,
  required,
  ariaLabel,
  accept,
  showDate,
  disabled,
  qaId,
  downloadAttachment,
  customMessage,
  maxSizeInMB,
  fileUploadStateAndActions,
}: Props) {
  const { i18n } = useI18n();

  const { fileId, fileMeta, loading, onFileSelected, onFileRemoved, error } =
    fileUploadStateAndActions;

  const { baseFilePickerInputStateAndHandlers } =
    useBaseFilePickerInputStateAndHandlers({ onFileSelected });

  return (
    <SmallFilePickerWrapper data-qa-id={qaId}>
      <BaseFilePickerInput
        name={name}
        qaId={qaId}
        required={required}
        ariaLabel={ariaLabel}
        accept={accept}
        disabled={disabled}
        stateAndHandlers={baseFilePickerInputStateAndHandlers}
      />

      <SmallFilePickerContent
        data-loading={loading ? true : undefined}
        data-mouse-active={
          baseFilePickerInputStateAndHandlers.mouseActive ? true : undefined
        }
      >
        {loading ? (
          <LoadingDots size="small" color="brandBlue" />
        ) : (
          <>
            <SmallFilePickerChooseOrDragItHere data-qa-id={`${qaId}-selector`}>
              <Icon name="paperclip" size={24} />

              <span>
                {customMessage ?? getBaseFilePickerDefaultMessage(i18n)}
              </span>
            </SmallFilePickerChooseOrDragItHere>

            {fileId ? (
              <SmallFilePickerFilename
                fileId={fileId}
                filename={fileMeta?.filename ?? null}
                qaId={qaId}
                onRemove={onFileRemoved}
                created={fileMeta && showDate ? fileMeta.created : null}
                downloadAttachment={downloadAttachment}
                disabled={disabled}
              />
            ) : (
              <SmallFilePickerNoFileChosenHelpText>
                {getBaseFilePickerHelpText(i18n, maxSizeInMB)}
              </SmallFilePickerNoFileChosenHelpText>
            )}
          </>
        )}
      </SmallFilePickerContent>

      {error && <FieldError errorMessage={error} />}
    </SmallFilePickerWrapper>
  );
}
